import { computed } from 'vue';
import { defineStore } from 'pinia';
import useCommonStore from '@/common/store/pinia/common';
import mapper from '@/common/store/pinia/fileStorageShares/fileStorageSharesMapper';
import api from '@/common/utils/api';
import { BackendTask, MpfsShare, TaskState } from '@schema';
import waitTask from '@/common/utils/waitTask';
import sequentialRequests from '@/common/utils/sequentialRequests';
import { TaskName } from '@/common/constants/taskNames.constants';
import { isString } from 'lodash-es';

export const useFileStorageSharesStore = defineStore('fileStorageShares', () => {
  const url = '/api/v2/mpfs/shares';

  const commonStore = useCommonStore<MpfsShare, MpfsShare>({
    getUrl: () => url,
    itemKey: 'share_id',
    plural: (data) => data.data,
    mapItem: mapper,
    updateMethod: 'put',
    dataChangeMode: 'task'
  })();

  const { getItems, changeItem, removeItem, remove, fetchItems, fetchItem } = commonStore;

  const fileStorageShares = computed(() => getItems.value);

  const startShare = (shareId: string) => api.post(`${url}/${shareId}/start`);

  const stopShare = (shareId: string) => api.post(`${url}/${shareId}/stop`);

  const deleteItems = (shareIds: ID[]) => sequentialRequests(
    shareIds.map(id => () => remove(id).then(waitTask))
  );

  const onMpfs = (data: any) => {
    const shareUpdated = data.object.share_updated || data.object.share_created;
    if (shareUpdated) {
      changeItem({
        ...shareUpdated,
        name: shareUpdated.share_name,
        status: shareUpdated.status || MpfsShare.status.CREATING
      });
    }
    if (data.object.share_deleted) {
      removeItem(data.object.share_deleted.share_id);
    }
  };

  const onTask = (data: BackendTask) => {
    if (data.name === TaskName.FILE_STORAGE_SHARE_CREATE && data.state === TaskState.SUCCESS) {
      fetchItems();
    }
    if (data.name === TaskName.FILE_STORAGE_SHARE_UPDATE && data.state === TaskState.SUCCESS) {
      const shareId = data.kwargs?.share_id;
      if (isString(shareId)) {
        fetchItem(shareId);
      }
    }
  };

  return {
    ...commonStore,
    fileStorageShares,
    startShare,
    stopShare,
    onMpfs,
    onTask,
    deleteItems
  };
}, {
  ws: {
    mpfs: 'onMpfs',
    tasks: 'onTask'
  }
});

export default useFileStorageSharesStore;
